import { useLayers, useOpenLayersContext } from '@ais-ui/react-openlayers';
import { getTargetInfoAtPixel, useSession, ExtractedTargetInfo } from '@ais-ui/websockets';
import { MapBrowserEvent } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { useEffect, useRef, useState } from 'react';
import { useAISMapContext } from './AISMapContext';
import { useViewMode, ViewMode } from '../../components/ViewModeProvider';
import { useSelectionState } from '../../components/SelectionStateProvider';

// Helper function to extract MMSI from target info
const getTargetMmsi = (item: ExtractedTargetInfo): string | undefined => {
  return (item.extractedTargetInfo[0] as any)?.mmsi ||
         (item.extractedTargetInfo[0] as any)?.methydMmsi;
};

// Find which ship was actually clicked by comparing against current selection
const findClickedTarget = (
  targetInfoFromClick: ExtractedTargetInfo,
  currentList: ExtractedTargetInfo[]
): any => {
  const existingMmsis = new Set(currentList.map(getTargetMmsi));
  
  return targetInfoFromClick.extractedTargetInfo.find((target: any) => {
    const targetMmsi = target?.mmsi || target?.methydMmsi;
    return !existingMmsis.has(targetMmsi);
  });
};

// Add clicked ship to selection list (ctrl+click behavior)
const addToSelection = (
  prevList: ExtractedTargetInfo[],
  targetInfoFromClick: ExtractedTargetInfo
): ExtractedTargetInfo[] => {
  const clickedTarget = findClickedTarget(targetInfoFromClick, prevList);
  
  if (!clickedTarget) {
    // All returned targets already in list - move first one to top
    const firstMmsi = getTargetMmsi({
      extractedTargetInfo: targetInfoFromClick.extractedTargetInfo,
      extractedTargetInfoType: targetInfoFromClick.extractedTargetInfoType,
    });
    
    const filteredList = prevList.filter((item) => getTargetMmsi(item) !== firstMmsi);
    const targetToAdd: ExtractedTargetInfo = {
      extractedTargetInfo: [targetInfoFromClick.extractedTargetInfo[0]],
      extractedTargetInfoType: targetInfoFromClick.extractedTargetInfoType,
    };
    
    return [targetToAdd, ...filteredList];
  }
  
  // Found new ship - add it to the list
  const newMmsi = clickedTarget?.mmsi || clickedTarget?.methydMmsi;
  const filteredList = prevList.filter((item) => getTargetMmsi(item) !== newMmsi);
  
  const newTargetInfo: ExtractedTargetInfo = {
    extractedTargetInfo: [clickedTarget],
    extractedTargetInfoType: targetInfoFromClick.extractedTargetInfoType,
  };
  
  return [newTargetInfo, ...filteredList];
};

export function useClickOnMap(enabled = true) {
  const [clickPosition, setClickPosition] = useState<Coordinate | null>(null);
  const [isCtrlClick, setIsCtrlClick] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { setIsClickingShip } = useAISMapContext();
  const { map } = useOpenLayersContext();
  const { isShipLayerVisible } = useLayers();

  useEffect(() => {
    const onClick = (e: MapBrowserEvent<UIEvent>) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Cannot click ships if the ship layer is not visible
      if (!isShipLayerVisible) {
        return;
      }

      // Immediately disable hover to prevent interference
      setIsClickingShip(true);

      // Check if Ctrl key is pressed during the click
      const originalEvent = e.originalEvent as MouseEvent;
      const ctrlPressed = originalEvent.ctrlKey;
      setIsCtrlClick(ctrlPressed);
      setClickPosition(e.coordinate);

      // Shorter timeout - just enough to prevent hover interference during click processing
      timeoutRef.current = setTimeout(() => {
        setIsClickingShip(false);
      }, 500); // Reduced from 2000ms
    };

    if (enabled) {
      map.on('click', onClick);
    } else {
      map.un('click', onClick);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      map.un('click', onClick);
    };
  }, [enabled, map, setIsClickingShip, isShipLayerVisible]);

  return { clickPosition, setClickPosition, isCtrlClick };
}

export function ListenForShipClicks() {
  const { clickPosition, isCtrlClick } = useClickOnMap();
  const { setTrigger } = useAISMapContext();
  const { setTargetInfoDetailsList } = useSelectionState();
  const { viewMode } = useViewMode();
  const { olSession, replaySession } = useSession();
  const { map } = useOpenLayersContext();
  const [isProcessingClick, setIsProcessingClick] = useState(false);
  const lastProcessedClick = useRef<Coordinate | null>(null);
  const lastProcessedTime = useRef<number>(0);

  const session =
    viewMode === ViewMode.Replay && replaySession ? replaySession : olSession;

  useEffect(() => {
    if (!clickPosition || isProcessingClick) {
      return;
    }
    // isProcessingClick intentionally not in deps - it's a guard flag, not a trigger

    // Allow reprocessing same coordinate if enough time has passed (500ms)
    const now = Date.now();
    if (
      lastProcessedClick.current &&
      lastProcessedClick.current[0] === clickPosition[0] &&
      lastProcessedClick.current[1] === clickPosition[1] &&
      now - lastProcessedTime.current < 500
    ) {
      return;
    }

    const processClick = async () => {
      setIsProcessingClick(true);
      lastProcessedClick.current = clickPosition;
      lastProcessedTime.current = now;

      try {
        // Convert coordinate to pixels immediately when processing click
        const [xFloat, yFloat] = map.getPixelFromCoordinate(clickPosition);
        const pixelCoordinate = {
          x: Math.round(xFloat),
          y: Math.round(yFloat),
        };

        // Direct API call - no cache interference
        const targetInfoFromClick = await getTargetInfoAtPixel(
          session,
          pixelCoordinate.x,
          pixelCoordinate.y,
          true, // selectTarget
          !isCtrlClick // deselectOtherTargets - Shows the track on multiple targets if clicking with Ctrl
        );

        if (
          !targetInfoFromClick ||
          targetInfoFromClick.extractedTargetInfo.length === 0
        ) {
          // No target found at click position
          if (isCtrlClick) {
            // Keep existing selection for Ctrl+click on empty space
            setTargetInfoDetailsList((prevList) => prevList);
          } else {
            // Clear selection for regular click on empty space
            setTargetInfoDetailsList([]);
          }
        } else {
          // Target found - add or replace selection
          if (isCtrlClick) {
            setTargetInfoDetailsList((prevList) => 
              addToSelection(prevList, targetInfoFromClick)
            );
          } else {
            // Regular click - replace selection with just the first target
            const clickedTarget: ExtractedTargetInfo = {
              extractedTargetInfo: [targetInfoFromClick.extractedTargetInfo[0]],
              extractedTargetInfoType: targetInfoFromClick.extractedTargetInfoType,
            };
            setTargetInfoDetailsList([clickedTarget]);
          }
        }

        // Trigger update after processing
        setTimeout(() => {
          setTrigger((prevTrigger) => prevTrigger + 1);
        }, 60);
      } catch (error) {
        console.error('Error processing ship click:', error);
      } finally {
        setIsProcessingClick(false);
      }
    };

    processClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickPosition, isCtrlClick, session, map, setTargetInfoDetailsList, setTrigger]);

  return null;
}
